import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { NewsCard } from "@/components/NewsCard";
import { Search, SlidersHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = {
  "stand-tasarimi": "Stand Tasarımı",
  "lojistik": "Lojistik",
  "sektor-trendleri": "Sektör Trendleri",
  "teknoloji": "Teknoloji",
  "surdurulebilirlik": "Sürdürülebilirlik",
  "pazar-analizi": "Pazar Analizi",
};

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const doSearch = async (q, cat) => {
    setLoading(true);
    setSearched(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      if (cat) params.set("category", cat);
      const res = await axios.get(`${API}/articles?${params.toString()}`);
      setArticles(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = searchParams.get("q");
    const cat = searchParams.get("category");
    if (q || cat) {
      setQuery(q || "");
      setCategory(cat || "");
      doSearch(q, cat);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    const params = {};
    if (query) params.q = query;
    if (category) params.category = category;
    setSearchParams(params);
    doSearch(query, category);
  };

  const handleCategorySelect = (slug) => {
    setCategory(slug);
    const params = {};
    if (query) params.q = query;
    if (slug) params.category = slug;
    setSearchParams(params);
    doSearch(query, slug);
  };

  return (
    <div data-testid="search-page" className="max-w-screen-2xl mx-auto px-4 md:px-8 lg:px-12 py-12 md:py-24">
      {/* Header */}
      <div className="mb-12">
        <span className="font-mono text-xs uppercase tracking-widest text-[#1B4F72] mb-3 block">
          Haber Ara
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95] text-stone-950 mb-4">
          Arama
        </h1>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 mb-12" data-testid="search-form">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Anahtar kelime ile arayın..."
            className="pl-10 rounded-none border-stone-300 h-12 text-base"
            data-testid="search-input"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="rounded-none border-stone-300 h-12 gap-2 font-mono text-xs uppercase tracking-widest"
              data-testid="category-filter-dropdown"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {category ? CATEGORIES[category] : "Kategori"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="rounded-none">
            <DropdownMenuItem
              onClick={() => handleCategorySelect("")}
              className="font-mono text-xs uppercase tracking-widest"
            >
              Tümü
            </DropdownMenuItem>
            {Object.entries(CATEGORIES).map(([slug, name]) => (
              <DropdownMenuItem
                key={slug}
                onClick={() => handleCategorySelect(slug)}
                className="font-mono text-xs uppercase tracking-widest"
              >
                {name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          type="submit"
          className="rounded-none h-12 px-8 bg-stone-900 text-white hover:bg-[#1B4F72] transition-colors font-mono text-xs uppercase tracking-widest"
          data-testid="search-submit"
        >
          Ara
        </Button>
      </form>

      {/* Results */}
      {loading ? (
        <div className="masonry-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-stone-200 p-6">
              <div className="skeleton h-48 w-full mb-4" />
              <div className="skeleton h-4 w-3/4 mb-2" />
              <div className="skeleton h-3 w-full" />
            </div>
          ))}
        </div>
      ) : searched ? (
        articles.length > 0 ? (
          <>
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-8" data-testid="search-results-count">
              {articles.length} sonuç bulundu
            </p>
            <div className="masonry-grid" data-testid="search-results">
              {articles.map((article, idx) => (
                <div key={article.id} className={`animate-fade-up stagger-${(idx % 5) + 1}`}>
                  <NewsCard article={article} />
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-24" data-testid="no-results">
            <p className="text-2xl font-semibold text-stone-400 mb-2">Sonuç Bulunamadı</p>
            <p className="text-stone-500">Farklı anahtar kelimeler veya kategorilerle tekrar deneyin.</p>
          </div>
        )
      ) : (
        <div className="text-center py-24">
          <p className="text-xl text-stone-400">Aramaya başlamak için yukarıdaki alanı kullanın.</p>
        </div>
      )}
    </div>
  );
}
